import { site } from '@panel/commons/helpers';
import * as yup from 'yup';

const OPTIONAL_COVERED_AREA = Object.freeze({
  ZPAR: ['40', '3', '20', '24', '26', '2004', '14'],
  RELA: ['40', '3', '20', '24', '26'],
  BRASIL: ['40', '1003'],
  MEX: ['14', '3', '20', '24']
});

const portalId = site.id;

const isNumericInt = num => /^-?[0-9.,]+$/.test(num);

const genericFormValidation = yup.object().shape({
  CFT101: yup
    .object({
      value: yup.number().when('$realEstateTypeId', (realEstateTypeId, schema) => {
        let verticalCode;
        if (portalId === 'ZPAR') {
          verticalCode = portalId;
        } else if (portalId.includes('BR')) {
          verticalCode = 'BRASIL';
        } else {
          verticalCode = 'RELA';
        }
        return OPTIONAL_COVERED_AREA[verticalCode].includes(realEstateTypeId)
          ? schema
              .min(0, 'publisher.step1.module4.area.covered.validation')
              .transform((v, o) => (o === '' ? 0 : parseInt(o)))
              .nullable()
              .notRequired()
          : schema
              .min(1, 'publisher.step1.module4.area.covered.validation')
              .transform((v, o) => (o === '' ? 0 : parseInt(o)))
              .required('publisher.step1.module4.area.covered.validation');
      }),
      value_unit: yup
        .string()
        .oneOf(['1', '2'])
        .required()
    })
    .required(),
  CFT5: yup
    .number()
    .min(-1)
    .required(),
  numberOfYears: yup.number().when('CFT5', {
    is: val => val === 1,
    then: yup
      .number()
      .min(1)
      .max(999)
      .required(),
    otherwise: yup
      .number()
      .nullable()
      .strip()
  }),
  CFT6: yup
    .object({
      value: yup
        .number()
        .integer()
        .min(0)
        .transform((v, o) => (o === '' ? 0 : parseInt(o.replace(/[,.]/g, ''))))
        .when('$expensesMaxValue', (max, schema) => (max ? schema.max(max, max) : schema)),
      value_unit: yup.string()
    })
    .notRequired(),
  CFT400: yup
    .object({
      value: yup
        .number()
        .integer()
        .min(0)
        .transform((v, o) => (o === '' ? 0 : parseInt(o.replace(/[,.]/g, ''))))
        .when('$iptuMaxValue', (max, schema) => (max ? schema.max(max, max) : schema)),
      value_unit: yup.string()
    })
    .notRequired(),
  title: yup
    .string()
    .required()
    .min(10)
    .max(80),
  description: yup
    .string()
    .min(150)
    .max(10000)
});

const commonTotalAreaValidation = yup.object().when(['$realEstateTypeId', 'CFT101'], (realEstateTypeId, CFT101) => {
  let verticalCode;
  if (portalId === 'ZPAR') {
    verticalCode = portalId;
  } else if (portalId.includes('BR')) {
    verticalCode = 'BRASIL';
  } else {
    verticalCode = 'RELA';
  }
  return OPTIONAL_COVERED_AREA[verticalCode].includes(realEstateTypeId)
    ? yup.object({
        value: yup
          .number()
          .transform((v, o) => (o === '' ? 0 : parseInt(o)))
          .integer()
          .min(1, 'publisher.step1.module4.area.total.validation')
          .required('publisher.step1.module4.area.total.validation'),
        value_unit: yup
          .string()
          .oneOf(['1', '2'])
          .required()
      })
    : yup.object({
        value: yup
          .number()
          .transform((v, o) => (o === '' ? 0 : parseInt(o)))
          .integer()
          .min(1)
          .moreThan(CFT101.value - 1, 'publisher.step1.module4.area.total.validation')
          .required('publisher.step1.module4.area.total.validation'),
        value_unit: yup
          .string()
          .oneOf(['1', '2'])
          .required()
      });
});

const priceCommon = yup.object({
  value: yup
    .number()
    .integer()
    .min(0)
    .transform((v, o) => parseInt(o.replace(/[,.]/g, '')))
    .required(),
  value_unit: yup.string().required()
});

const priceValueNonZero = yup.object({
  value: yup
    .number()
    .integer()
    .min(1)
    .transform((v, o) => parseInt(o.replace(/[,.]/g, '')))
    .required(),
  value_unit: yup.string().required()
});

const pricePeru = yup
  .array()
  .min(2)
  .of(
    yup.lazy((priceItem, options) => {
      const prices = typeof options.value === 'undefined' ? options.parent : options.value;
      const priceItemIndex = prices.findIndex(
        i => i.value === priceItem.value && i.value_unit === priceItem.value_unit
      );
      const prev = priceItemIndex !== 0 ? prices[priceItemIndex - 1] : null;
      const precioUSD = prices[1];

      if (prev === null) {
        const precioPEN = prices[0];
        return yup.object({
          value:
            precioUSD.value > 0
              ? yup
                  .number()
                  .integer()
                  .moreThan(
                    parseInt(precioUSD.value, 10),
                    parseInt(precioPEN.value, 10) === 0
                      ? 'publisher.step1.module4.price.validation.localCoin'
                      : 'publisher.step1.module4.price.validation.usdCoin'
                  )
                  .transform((v, o) => parseInt(o.replace(/,/g, '')))
                  .required('publisher.step1.module4.price.validation.localCoin')
              : yup
                  .number()
                  .integer()
                  .min(0)
                  .transform((v, o) => parseInt(o.replace(/,/g, '')))
                  .required(),
          value_unit: yup.string().required()
        });
      }
      if (!isNumericInt(precioUSD.value)) {
        return yup.object({
          value: yup
            .mixed()
            .transform((v, o) => 0)
            .notRequired()
        });
      }
      return yup.object({
        value: yup
          .number()
          .integer()
          .min(0)
          .transform((v, o) => parseInt(o.replace(/,/g, ''))),
        value_unit: yup.string().required()
      });
    })
  );

const totalAreaValidationPE = yup.object().when('$realEstateTypeId', realEstateTypeId => {
  return OPTIONAL_COVERED_AREA.RELA.includes(realEstateTypeId)
    ? yup
        .object({
          value: yup
            .number()
            .transform((v, o) => (o === '' ? 0 : parseInt(o)))
            .integer()
            .min(1, 'publisher.step1.module4.area.total.validation')
            .required('publisher.step1.module4.area.total.validation'),
          value_unit: yup
            .string()
            .oneOf(['1', '2'])
            .required()
        })
        .required()
    : yup
        .object({
          value: yup
            .number()
            .transform((v, o) => (o === '' ? 0 : parseInt(o)))
            .integer()
            .min(0, 'publisher.step1.module4.area.total.validation')
            .notRequired(),
          value_unit: yup
            .string()
            .oneOf(['1', '2'])
            .notRequired()
        })
        .notRequired();
});

const totalAreaValidationMX = yup.object().when('$realEstateTypeId', realEstateTypeId => {
  return OPTIONAL_COVERED_AREA.MEX.includes(realEstateTypeId)
    ? yup
        .object({
          value: yup
            .number()
            .transform((v, o) => (o === '' ? 0 : parseInt(o)))
            .integer()
            .min(1, 'publisher.step1.module4.area.total.validation')
            .required(),
          value_unit: yup
            .string()
            .oneOf(['1', '2'])
            .notRequired()
        })
        .required()
    : yup
        .object({
          value: yup
            .number()
            .transform((v, o) => (o === '' ? 0 : parseInt(o)))
            .integer()
            .min(0, 'publisher.step1.module4.area.total.validation')
            .notRequired(),
          value_unit: yup
            .string()
            .oneOf(['1', '2'])
            .notRequired()
        })
        .notRequired();
});

const coveredAreaValidationMX = yup
  .object({
    value: yup.number().when('$realEstateTypeId', (realEstateTypeId, schema) => {
      return OPTIONAL_COVERED_AREA.MEX.includes(realEstateTypeId)
        ? schema
            .min(0, 'publisher.step1.module4.area.covered.validation')
            .transform((v, o) => (o === '' ? 0 : parseInt(o)))
            .nullable()
            .notRequired()
        : schema
            .min(1, 'publisher.step1.module4.area.covered.validation')
            .transform((v, o) => (o === '' ? 0 : parseInt(o)))
            .required('publisher.step1.module4.area.covered.validation');
    }),
    value_unit: yup
      .string()
      .oneOf(['1', '2'])
      .required()
  })
  .required();

function getCustomFormValidations(siteId = '') {
  const portalValidations = {
    ZPAR: yup.object().shape({
      CFT100: commonTotalAreaValidation,
      price: priceCommon
    }),
    ICBR: yup.object().shape({
      price: priceValueNonZero,
      CFT100: commonTotalAreaValidation
    }),
    WIBR: yup.object().shape({
      price: priceValueNonZero,
      CFT100: commonTotalAreaValidation
    }),
    IWBR: yup.object().shape({
      price: priceValueNonZero,
      CFT100: commonTotalAreaValidation
    }),
    '24MX': yup.object().shape({
      price: priceValueNonZero,
      CFT100: totalAreaValidationMX,
      CFT101: coveredAreaValidationMX
    }),
    VAMX: yup.object().shape({
      price: priceValueNonZero,
      CFT100: totalAreaValidationMX,
      CFT101: coveredAreaValidationMX
    }),
    UBPE: yup.object().shape({
      CFT100: totalAreaValidationPE,
      CFT5: yup
        .number()
        .min(-1)
        .required(),
      numberOfYears: yup.number().when('CFT5', {
        is: val => val === 1,
        then: yup
          .number()
          .min(1)
          .required(),
        otherwise: yup
          .number()
          .nullable()
          .strip()
      }),
      price: pricePeru
    }),
    DVPE: yup.object().shape({
      CFT100: totalAreaValidationPE,
      CFT5: yup
        .number()
        .min(-1)
        .required(),
      numberOfYears: yup.number().when('CFT5', {
        is: val => val === 1,
        then: yup
          .number()
          .min(1)
          .required(),
        otherwise: yup
          .number()
          .nullable()
          .strip()
      }),
      price: pricePeru
    }),
    PLEC: yup.object().shape({
      CFT100: commonTotalAreaValidation,
      CFT5: yup
        .number()
        .min(-1)
        .required(),
      numberOfYears: yup.number().when('CFT5', {
        is: val => val === 1,
        then: yup
          .number()
          .min(1)
          .required(),
        otherwise: yup
          .number()
          .nullable()
          .strip()
      }),
      price: priceValueNonZero
    }),
    CAPA: yup.object().shape({
      CFT100: commonTotalAreaValidation,
      CFT5: yup
        .number()
        .min(-1)
        .required(),
      numberOfYears: yup.number().when('CFT5', {
        is: val => val === 1,
        then: yup
          .number()
          .min(1)
          .required(),
        otherwise: yup
          .number()
          .nullable()
          .strip()
      }),
      price: priceValueNonZero
    }),
    LLVE: yup.object().shape({
      CFT100: commonTotalAreaValidation,
      CFT5: yup
        .number()
        .min(-1)
        .required(),
      numberOfYears: yup.number().when('CFT5', {
        is: val => val === 1,
        then: yup
          .number()
          .min(1)
          .required(),
        otherwise: yup
          .number()
          .nullable()
          .strip()
      }),
      price: priceCommon
    })
  };
  return portalValidations[siteId];
}

function getValidationSchema() {
  if (site.id) {
    if (['IWBR', '24MX', 'UBPE', 'DVPE', 'WIBR', 'IWBR', 'ICBR', 'PLEC', 'ZPAR', 'CAPA', 'VAMX'].includes(site.id)) {
      return genericFormValidation.concat(getCustomFormValidations(site.id));
    }
  }
  return genericFormValidation;
}

const Step1Module4Schema = getValidationSchema();

export { Step1Module4Schema };
